/**
 * Dialog for choosing how to apply updates to an absence series
 * Provides options: this occurrence only, future absences, or all absences
 */

import { useLingui } from '@lingui/react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

export type AbsenceUpdateScope = 'this_only' | 'future' | 'all';

export interface AbsenceSeriesUpdateChoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChoose: (scope: AbsenceUpdateScope) => Promise<void>;
  isPending: boolean;
}

export function AbsenceSeriesUpdateChoiceDialog({
  open,
  onOpenChange,
  onChoose,
  isPending,
}: AbsenceSeriesUpdateChoiceDialogProps) {
  const { i18n } = useLingui();

  const handleChoose = async (scope: AbsenceUpdateScope) => {
    await onChoose(scope);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {i18n._({
              id: 'absences.series.updateChoice.title',
              message: 'Update Series',
            })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {i18n._({
              id: 'absences.series.updateChoice.description',
              message: 'How would you like to apply these changes?',
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col gap-2 sm:flex-col">
          {/* This occurrence only */}
          <Button
            variant="outline"
            className="w-full justify-start text-left"
            onClick={() => handleChoose('this_only')}
            disabled={isPending}
            data-testid="update-this-only-button"
          >
            {isPending && (
              <Loader2
                className="mr-2 h-4 w-4 animate-spin"
                aria-hidden="true"
              />
            )}
            <div className="flex flex-col items-start">
              <span className="font-medium">
                {i18n._({
                  id: 'absences.series.updateChoice.thisOnly',
                  message: 'This occurrence only',
                })}
              </span>
              <span className="text-xs text-muted-foreground font-normal">
                {i18n._({
                  id: 'absences.series.updateChoice.thisOnlyDesc',
                  message: 'Mark this absence as individually modified',
                })}
              </span>
            </div>
          </Button>

          {/* Future absences */}
          <Button
            variant="outline"
            className="w-full justify-start text-left"
            onClick={() => handleChoose('future')}
            disabled={isPending}
            data-testid="update-future-button"
          >
            {isPending && (
              <Loader2
                className="mr-2 h-4 w-4 animate-spin"
                aria-hidden="true"
              />
            )}
            <div className="flex flex-col items-start">
              <span className="font-medium">
                {i18n._({
                  id: 'absences.series.updateChoice.future',
                  message: 'Future absences',
                })}
              </span>
              <span className="text-xs text-muted-foreground font-normal">
                {i18n._({
                  id: 'absences.series.updateChoice.futureDesc',
                  message: 'Update absences from this date onwards',
                })}
              </span>
            </div>
          </Button>

          {/* All absences in series */}
          <Button
            variant="default"
            className="w-full justify-start text-left"
            onClick={() => handleChoose('all')}
            disabled={isPending}
            data-testid="update-all-button"
          >
            {isPending && (
              <Loader2
                className="mr-2 h-4 w-4 animate-spin"
                aria-hidden="true"
              />
            )}
            <div className="flex flex-col items-start">
              <span className="font-medium">
                {i18n._({
                  id: 'absences.series.updateChoice.all',
                  message: 'All absences in series',
                })}
              </span>
              <span className="text-xs text-muted-foreground font-normal">
                {i18n._({
                  id: 'absences.series.updateChoice.allDesc',
                  message: 'Update all non-exception absences',
                })}
              </span>
            </div>
          </Button>

          <AlertDialogCancel
            className="w-full sm:w-auto"
            data-testid="cancel-button"
          >
            {i18n._({ id: 'common.cancel', message: 'Cancel' })}
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
